import { useEffect, useRef, useState } from "react";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchClient } from "../lib/api-client";

type CompleteState = "loading" | "success" | "error";

export function BookingPaySuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState<CompleteState>(
    sessionId ? "loading" : "error",
  );
  const [errorMessage, setErrorMessage] = useState(
    sessionId ? "" : "Missing payment session.",
  );
  const startedRef = useRef(false);

  useEffect(() => {
    if (!sessionId || startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      const { error } = await fetchClient.POST(
        "/api/public/payments/booking/complete",
        { body: { sessionId } },
      );

      if (error) {
        const message =
          typeof error === "object" &&
          error !== null &&
          "detail" in error &&
          typeof error.detail === "string"
            ? error.detail
            : "We could not confirm your payment. If you were charged, please contact the club.";
        setErrorMessage(message);
        setState("error");
        return;
      }

      setState("success");
    })();
  }, [sessionId]);

  if (state === "loading") {
    return (
      <div className="max-w-lg mx-auto space-y-8 text-center">
        <div className="flex justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Confirming payment
          </h1>
          <p className="text-gray-600 text-lg">
            Please wait while we record your payment.
          </p>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="max-w-lg mx-auto space-y-8 text-center">
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment confirmation failed
          </h1>
          <p className="text-gray-600 text-lg">{errorMessage}</p>
        </div>
        <Link
          to="/"
          className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
        >
          Return home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-8 text-center">
      <div className="flex justify-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
      </div>
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Payment successful
        </h1>
        <p className="text-gray-600 text-lg">
          Thank you. Your booking payment has been received.
        </p>
      </div>
      <Link
        to="/"
        className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
      >
        Return home
      </Link>
    </div>
  );
}
